import React, { useContext, useEffect, useState, useRef } from 'react';
import { GlobalContext } from '../library/Core/GlobalContext';
import AppSettingsService from '../services/app-settings.service';
import SessionContext from '../library/Core/SessionContext';
import { isUrlAllowedInLimitedAccess } from '../library/Core/SmartFunctions';
import Swal from 'sweetalert2';

const ProtectedRoute: React.FC<any> = ({ children }) => {
    const { globalState } = useContext(GlobalContext);
    const { sessionState } = useContext(SessionContext);
    const [isAccessDenied, setIsAccessDenied] = useState(false);
    const previousPathRef = useRef<string>(window.location.pathname + window.location.search);

    const excludePaths = ['login', 'signup'];
    const userLoggedOut = sessionStorage.getItem('user-logged-out') === 'TRUE' ? true : false;

    const pathSegments = window.location.pathname.split('/');
    const currentPathSegment = pathSegments[pathSegments.length - 1] || '';
    const isExcludedPath = excludePaths.includes(currentPathSegment);

    // @UAS-2071: Check if URL is allowed in LIMITED_ACCESS mode
    useEffect(() => {
        const checkUrlAccess = () => {
            const currentPath = window.location.pathname + window.location.search;
            
            // Only check if path has actually changed
            // if (currentPath === previousPathRef.current && isAccessDenied === false) {
            //     return;
            // }

            previousPathRef.current = currentPath;
            
            if (!isUrlAllowedInLimitedAccess(currentPath)) {
                setIsAccessDenied(true);

                Swal.fire({
                    icon: 'warning',
                    title: 'Access Denied..!',
                    text: 'You can not access this page in impersonation mode..!',
                    showConfirmButton: false,
                    allowOutsideClick: false,
                });
            } else {
                setIsAccessDenied(false);
                // if previously shown, close the alert
                if (Swal.isVisible()) {
                    Swal.close();
                }
            }
        };

        // Check on initial mount
        checkUrlAccess();

        // Listen for browser back/forward navigation
        window.addEventListener('popstate', checkUrlAccess);
        
        // Listen for hash changes
        window.addEventListener('hashchange', checkUrlAccess);

        // Override history.pushState to detect programmatic navigation
        const originalPushState = window.history.pushState;
        window.history.pushState = function (...args) {
            originalPushState.apply(window.history, args);
            checkUrlAccess();
        };

        // Override history.replaceState to detect state changes
        const originalReplaceState = window.history.replaceState;
        window.history.replaceState = function (...args) {
            originalReplaceState.apply(window.history, args);
            checkUrlAccess();
        };

        // Polling fallback - check URL every 500ms
        const intervalId = setInterval(() => {
            if (window.location.pathname + window.location.search !== previousPathRef.current) {
                checkUrlAccess();
            }
        }, 500);

        return () => {
            window.removeEventListener('popstate', checkUrlAccess);
            window.removeEventListener('hashchange', checkUrlAccess);
            window.history.pushState = originalPushState;
            window.history.replaceState = originalReplaceState;
            clearInterval(intervalId);
        };
    }, []);

    // listen
    useEffect(() => {
        const channel = new BroadcastChannel('auth');
    
        channel.onmessage = (event) => {
            if (event.data.type === 'REDIRECT_CLOSE_TAB') {
                const isActiveTab = sessionStorage.getItem('active-tab') == 'TRUE';

                sessionStorage.removeItem('active-tab');
                // If this is NOT the active tab, close it
                if (!isActiveTab) {
                    window.close();
                } else {
                    // Only navigate on the active tab
                    window.location.href = event.data.path;
                }
            }
        };
    
        return () => {
            channel.close();
        };
        }, []);

    if (!isExcludedPath && userLoggedOut) {
        sessionStorage.setItem('user-logged-out', 'FALSE');
        // const loginUrl = AppSettingsService.getLoginUrlFromStorage();
        const loginUrl = AppSettingsService.getLoginUrl(sessionState);
        if (loginUrl) window.location.href = loginUrl;
    }

    if (isAccessDenied) {
        return <></>;
    }

    return <>{children}</>;
};

export default ProtectedRoute;
